import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AuthUser,
  PrivateQueueEntrySummary,
  PrivateQueueOptions,
  ShowSummary,
  ShowTypeInfo,
} from '@fetmode/types';
import type { ApiClient } from '@fetmode/api-client';
import { ApiError } from '@fetmode/api-client';
import { useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import {
  FAN_PRIVATE_QUEUE_TYPES,
  computePrivateQueueEstimate,
  isPrivateQueueAvailable,
} from '@fetmode/utils';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';

import { PrivateShowRequestModal } from './PrivateShowRequestModal.js';
import { PrivateShowRfpModal } from './PrivateShowRfpModal.js';
import { RoomBarGroup } from './RoomBarGroup.js';
import { canUpgradeVipToExclusive, resolvePrivateShowPanelMode } from './show-join-gate.js';

const QUEUE_POLL_MS = 2_500;

export interface PrivateShowPanelProps {
  show: ShowSummary;
  showId: string;
  user: AuthUser | null;
  api: ApiClient;
  onError: (message: string | null) => void;
  requestOpen?: boolean;
  onRequestOpenChange?: (open: boolean) => void;
  onOutcome?: (message: string) => void;
  onBalanceRefresh?: () => Promise<void> | void;
  onShowRefresh?: () => Promise<void>;
  onSessionEnded?: (result: { returnedToPublic: boolean }) => void;
  onEndedForFeedback?: () => void;
}

export function PrivateShowPanel({
  show,
  showId,
  user,
  api,
  onError,
  requestOpen,
  onRequestOpenChange,
  onOutcome,
  onBalanceRefresh,
  onShowRefresh,
  onSessionEnded,
  onEndedForFeedback,
}: PrivateShowPanelProps): ReactElement | null {
  const { requireAuth } = useAuthModal();
  const { requireSpendCoins } = useCoinShop();
  const [showTypes, setShowTypes] = useState<ShowTypeInfo[]>([]);
  const [queueOptions, setQueueOptions] = useState<PrivateQueueOptions | null>(null);
  const [queueType, setQueueType] = useState<'VIP' | 'EXCLUSIVE'>('VIP');
  const [durationMinutes, setDurationMinutes] = useState<number | null>(null);
  const [timingMode, setTimingMode] = useState<'asap' | 'schedule'>('asap');
  const [scheduledAt, setScheduledAt] = useState('');
  const [queueEntries, setQueueEntries] = useState<PrivateQueueEntrySummary[]>([]);
  const [queueEntry, setQueueEntry] = useState<PrivateQueueEntrySummary | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [rfpOpen, setRfpOpen] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const pendingEntryIdRef = useRef<string | null>(null);
  const wasPrivateFloorRef = useRef(show.type === 'VIP' || show.type === 'EXCLUSIVE');

  const isModalOpen = requestOpen ?? internalModalOpen;

  function setModalOpen(open: boolean): void {
    if (onRequestOpenChange) {
      onRequestOpenChange(open);
    } else {
      setInternalModalOpen(open);
    }
  }

  const findMine = useCallback(
    (entries: PrivateQueueEntrySummary[]): PrivateQueueEntrySummary | null => {
      if (!user) {
        return null;
      }
      return (
        entries.find((entry) => {
          if (entry.userId === user.id) {
            return true;
          }
          if (!user.username) {
            return false;
          }
          return entry.username.toLowerCase() === user.username.toLowerCase();
        }) ?? null
      );
    },
    [user],
  );

  const loadQueue = useCallback(async () => {
    const result = await api.listPrivateQueue(showId);
    setQueueEntries(result.entries);
    const mine = findMine(result.entries);
    setQueueEntry(mine);

    const pendingId = pendingEntryIdRef.current;
    if (pendingId && !mine) {
      pendingEntryIdRef.current = null;
      const acceptedNow = show.type === 'VIP' || show.type === 'EXCLUSIVE';
      if (acceptedNow) {
        onOutcome?.(t('common.show.privateShowAccepted'));
      } else {
        onOutcome?.(t('common.show.privateShowDeclined'));
        void onBalanceRefresh?.();
      }
    } else if (mine) {
      pendingEntryIdRef.current = mine.id;
    }
  }, [api, findMine, onBalanceRefresh, onOutcome, show.type, showId]);

  const loadOptions = useCallback(async () => {
    try {
      const result = await api.getPrivateQueueOptions(showId);
      setQueueOptions(result.options);
      setQueueType(result.options.defaultQueueType);
      setOptionsError(null);
    } catch (err: unknown) {
      setQueueOptions(null);
      setOptionsError(
        err instanceof ApiError ? err.message : t('common.show.privateShowOptionsUnavailable'),
      );
    }
  }, [api, showId]);

  useEffect(() => {
    void api.listShowTypes().then((result) => {
      setShowTypes(result.types);
    });
  }, [api]);

  useEffect(() => {
    const isPrivateFloor = show.type === 'VIP' || show.type === 'EXCLUSIVE';
    if (!wasPrivateFloorRef.current && isPrivateFloor && pendingEntryIdRef.current) {
      pendingEntryIdRef.current = null;
      onOutcome?.(t('common.show.privateShowAccepted'));
    }
    // Model accepted / started the private — the caller who was in line is now in-session.
    if (!wasPrivateFloorRef.current && isPrivateFloor) {
      setQueueEntry(null);
    }
    wasPrivateFloorRef.current = isPrivateFloor;

    if (!isPrivateQueueAvailable(show)) {
      setQueueEntries([]);
      setQueueEntry(null);
      return;
    }

    void loadOptions().catch(() => undefined);
    void loadQueue().catch(() => undefined);

    const intervalId = window.setInterval(() => {
      void loadQueue().catch(() => undefined);
    }, QUEUE_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadOptions, loadQueue, onOutcome, show, show.status, show.type]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    setJoinError(null);
    void loadOptions().catch(() => undefined);
    void loadQueue().catch(() => undefined);
  }, [isModalOpen, loadOptions, loadQueue]);

  const panelMode = resolvePrivateShowPanelMode(show);
  const canUpgrade = canUpgradeVipToExclusive(show);
  const exclusiveRate = queueOptions?.exclusiveCoinsPerMinute ?? null;
  const privateTypes = showTypes.filter((type) => {
    if (!FAN_PRIVATE_QUEUE_TYPES.includes(type.type as (typeof FAN_PRIVATE_QUEUE_TYPES)[number])) {
      return false;
    }
    if (type.type === 'VIP') {
      return queueOptions?.vipEnabled !== false;
    }
    if (type.type === 'EXCLUSIVE') {
      return queueOptions?.exclusiveEnabled !== false;
    }
    return true;
  });

  async function handleEndShow(): Promise<void> {
    setIsEnding(true);
    onError(null);
    try {
      const result = await api.leaveShow(showId);
      await onShowRefresh?.();
      onSessionEnded?.({ returnedToPublic: Boolean(result.returnedToPublic) });
      onEndedForFeedback?.();
    } catch (err: unknown) {
      onError(err instanceof ApiError ? err.message : t('common.show.endShowUnable'));
    } finally {
      setIsEnding(false);
    }
  }

  async function handleUpgradeExclusive(): Promise<void> {
    setIsUpgrading(true);
    onError(null);
    try {
      await api.upgradeExclusive(showId);
      await onShowRefresh?.();
    } catch (err: unknown) {
      onError(err instanceof ApiError ? err.message : t('common.show.upgradeExclusiveUnable'));
    } finally {
      setIsUpgrading(false);
    }
  }

  async function handleJoinQueue(): Promise<void> {
    if (queueOptions) {
      const rate =
        queueType === 'EXCLUSIVE'
          ? queueOptions.exclusiveCoinsPerMinute
          : queueOptions.vipCoinsPerMinute;
      const discountPercent =
        durationMinutes === null
          ? 0
          : (queueOptions.durationTiers.find((tier) => tier.minutes === durationMinutes)
              ?.discountPercent ?? 0);
      const { depositCoins } = computePrivateQueueEstimate({
        ratePerMinute: rate,
        durationMinutes,
        discountPercent,
        untimedDepositMinutes: queueOptions.untimedDepositMinutes,
        queueDepositPercent: queueOptions.queueDepositPercent,
        preTipCoins:
          queueType === 'EXCLUSIVE' ? queueOptions.exclusivePreTipCoins : queueOptions.vipPreTipCoins,
      });
      if (!(await requireSpendCoins(depositCoins, () => {
        void handleJoinQueue();
      }))) {
        return;
      }
    }

    setIsJoining(true);
    setJoinError(null);
    onError(null);

    try {
      const result = await api.joinPrivateQueue(showId, {
        type: queueType,
        durationMinutes,
        scheduledAt:
          timingMode === 'schedule' && scheduledAt.length > 0
            ? new Date(scheduledAt).toISOString()
            : null,
      });
      setQueueEntry(result.entry);
      pendingEntryIdRef.current = result.entry.id;
      await loadQueue();
      setModalOpen(false);
    } catch (err: unknown) {
      const message = err instanceof ApiError ? err.message : t('common.show.unableToJoinQueue');
      setJoinError(message);
      onError(message);
    } finally {
      setIsJoining(false);
    }
  }

  function handleLeaveQueue(): void {
    setIsJoining(true);
    onError(null);
    void api
      .leavePrivateQueue(showId)
      .then(async () => {
        pendingEntryIdRef.current = null;
        setQueueEntry(null);
        await loadQueue();
        setModalOpen(false);
      })
      .catch((err: unknown) => {
        onError(err instanceof ApiError ? err.message : 'Unable to leave queue.');
      })
      .finally(() => {
        setIsJoining(false);
      });
  }

  const requestModal = (
    <PrivateShowRequestModal
      isOpen={isModalOpen}
      onClose={() => {
        setModalOpen(false);
      }}
      privateTypes={privateTypes}
      queueOptions={queueOptions}
      queueType={queueType}
      onQueueTypeChange={setQueueType}
      durationMinutes={durationMinutes}
      onDurationMinutesChange={setDurationMinutes}
      timingMode={timingMode}
      onTimingModeChange={setTimingMode}
      scheduledAt={scheduledAt}
      onScheduledAtChange={setScheduledAt}
      isJoining={isJoining}
      onJoin={() => {
        void handleJoinQueue();
      }}
      joinError={joinError}
      optionsError={optionsError}
      queueEntry={queueEntry}
      queueEntries={queueEntries}
      showQueueList
      onLeaveQueue={handleLeaveQueue}
      isSignedIn={user !== null}
      modelInPrivateLabel={null}
      canRequest
    />
  );

  const rfpModal = (
    <PrivateShowRfpModal
      isOpen={rfpOpen}
      onClose={() => {
        setRfpOpen(false);
      }}
      modelUsername={show.model.username}
      requireSchedule={false}
      vipEnabled={queueOptions?.vipEnabled}
      exclusiveEnabled={queueOptions?.exclusiveEnabled}
      vipPreTipCoins={queueOptions?.vipPreTipCoins}
      exclusivePreTipCoins={queueOptions?.exclusivePreTipCoins}
    />
  );

  if (panelMode === 'session') {
    return (
      <RoomBarGroup label={t('common.show.sessionGroupLabel')} variant="session">
        <Button
          label={isEnding ? t('common.show.endShowEnding') : t('common.show.endShowButton')}
          variant="request"
          disabled={isEnding || isUpgrading}
          onClick={() => {
            void handleEndShow();
          }}
        />
        {canUpgrade ? (
          <Button
            label={
              isUpgrading
                ? t('common.show.upgradeExclusiveUpgrading')
                : exclusiveRate !== null
                  ? t('common.show.upgradeExclusiveRate').replace('{{amount}}', String(exclusiveRate))
                  : t('common.show.upgradeExclusiveButton')
            }
            variant="primary"
            disabled={isEnding || isUpgrading}
            onClick={() => {
              void handleUpgradeExclusive();
            }}
          />
        ) : null}
      </RoomBarGroup>
    );
  }

  if (panelMode === 'hidden') {
    return null;
  }

  if (panelMode === 'queue-modal') {
    return (
      <>
        {requestModal}
        {rfpModal}
      </>
    );
  }

  const triggerLabel = queueEntry
    ? queueEntries.length <= 1
      ? t('common.show.privateShowRequestingButton')
      : t('common.show.privateShowQueueButton').replace(
          '{{position}}',
          String(queueEntry.position),
        )
    : t('common.show.privateShowRequestButton');

  return (
    <>
      <RoomBarGroup label={t('common.show.privateGroupLabel')} variant="session">
        <Button
          label={triggerLabel}
          variant={queueEntry ? 'primary' : 'request'}
          onClick={() => {
            requireAuth(() => {
              setJoinError(null);
              setModalOpen(true);
              void loadOptions().catch(() => undefined);
            }, 'private');
          }}
        />
        <Button
          label={t('common.show.privateRfpScheduleButton')}
          variant="ghost"
          onClick={() => {
            requireAuth(() => {
              setRfpOpen(true);
            }, 'private');
          }}
        />
      </RoomBarGroup>

      {requestModal}
      {rfpModal}
    </>
  );
}
